import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useResponseFormatting } from './ResponseFormattingContext';

interface LinkRendererProps {
  children: React.ReactNode;
  href: string;
  className?: string;
}

export const LinkRenderer: React.FC<LinkRendererProps> = ({ children, href, className = '' }) => {
  const { settings } = useResponseFormatting();

  if (!settings.autoDetectLinks) {
    return <span className={className}>{children}</span>;
  }

  const isExternalLink = href.startsWith('http') || href.startsWith('https');
  const target = settings.openLinksInNewTab && isExternalLink ? '_blank' : undefined;
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={`text-primary hover:text-primary/80 underline transition-colors inline-flex items-center gap-1 ${className}`}
    >
      {children}
      {isExternalLink && target === '_blank' && (
        <ExternalLink className="h-3 w-3 opacity-70" />
      )}
    </a>
  );
};

// Auto-link detection utility
export const autoLinkText = (text: string): React.ReactNode[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <LinkRenderer key={index} href={part}>
          {part}
        </LinkRenderer>
      );
    }
    return part;
  });
};